import React, { useState, useEffect } from 'react';
import { Calendar, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { DataTable, DataTableColumnHeader } from '../components/ui/data-table';
import ApiService from '../services/api.service';
import { isPlatformLevel, can } from '../utils/roleUtils';

const Tickets = ({ selectedCustomer, currentUser }) => {
  const isPlatform = isPlatformLevel(currentUser?.role);
  const PAGE_SIZE = 20;

  const [selectedTypingCenter, setSelectedTypingCenter] = useState(
    isPlatform ? 'ALL' : currentUser?.typingCenterId
  );
  const [typingCenters, setTypingCenters] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [page, setPage] = useState(0);
  const [lastFetchCount, setLastFetchCount] = useState(0);

  const [dateRange, setDateRange] = useState('30_DAYS');
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const getDateRange = () => {
    const formatYYYYMMDD = (date) => date.toISOString().slice(0, 10);
    const now = new Date();
    let startDate = null;
    let endDate = formatYYYYMMDD(now);

    switch (dateRange) {
      case '24_HR':
        startDate = formatYYYYMMDD(new Date(now.getTime() - 24 * 60 * 60 * 1000));
        break;
      case '7_DAYS':
        startDate = formatYYYYMMDD(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
        break;
      case '30_DAYS':
        startDate = formatYYYYMMDD(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
        break;
      case '90_DAYS':
        startDate = formatYYYYMMDD(new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000));
        break;
      case 'CUSTOM':
        startDate = customStartDate || null;
        endDate = customEndDate || formatYYYYMMDD(now);
        break;
      default:
        startDate = formatYYYYMMDD(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000));
    }

    return { startDate, endDate };
  };

  useEffect(() => {
    if (!isPlatform) return;
    ApiService.getTypingCenters()
      .then((response) => setTypingCenters(response.data?.data || []))
      .catch((err) => console.error('Failed to fetch typing centers:', err));
  }, [isPlatform]);

  // Fetch organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoadingOrganizations(true);
        const centerId = isPlatform
          ? (selectedTypingCenter === 'ALL' ? null : selectedTypingCenter)
          : currentUser?.typingCenterId;

        if (!centerId) {
          const response = await ApiService.get('/api/organisations');
          if (response.data && response.data.success) {
            setOrganizations(response.data.data || []);
          }
        } else {
          const response = await ApiService.get(`/api/typing-centers/${centerId}/with-mappings`);
          if (response.data && response.data.success) {
            const centerData = response.data.data;
            const mappedOrgs = centerData.mapped_organizations || centerData.mappedOrganizations || [];
            setOrganizations(mappedOrgs);
          }
        }
      } catch (err) {
        console.error('❌ [Tickets] Error fetching organizations:', err);
        setOrganizations([]);
      } finally {
        setLoadingOrganizations(false);
      }
    };

    if (currentUser) {
      fetchOrganizations();
    }
  }, [currentUser, selectedTypingCenter, isPlatform]);

  // Fetch tickets
  useEffect(() => {
    const fetchTicketsAndInvoices = async () => {
      try {
        setLoadingTickets(true);
        const params = {};

        const centerId = isPlatform
          ? (selectedTypingCenter === 'ALL' ? null : selectedTypingCenter)
          : currentUser?.typingCenterId;

        if (centerId) {
          params.typingCenterId = centerId;
        }

        if (selectedOrganization !== 'ALL') {
          params.organizationId = selectedOrganization;
        }

        const { startDate, endDate } = getDateRange();
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;

        params.page = page;
        params.pageSize = PAGE_SIZE;

        // Invoices are a separate feature: only fetch them when this user can read invoices.
        const [ticketsResponse, invoicesResponse] = await Promise.all([
          ApiService.getTickets(params),
          can(currentUser, 'invoice', 'read')
            ? ApiService.getInvoices()
            : Promise.resolve({ data: { success: false, data: [] } })
        ]);

        let fetchedTickets = [];
        if (ticketsResponse.data && ticketsResponse.data.success) {
          const payload = ticketsResponse.data.data;
          fetchedTickets = Array.isArray(payload)
            ? payload
            : (payload?.tickets || payload?.items || payload?.results || payload?.data || []);
          setLastFetchCount(fetchedTickets.length);
        } else {
          setLastFetchCount(0);
        }

        const ticketInvoiceMap = {};
        if (invoicesResponse.data && invoicesResponse.data.success) {
          const invoices = invoicesResponse.data.data || [];
          invoices.forEach(invoice => {
            const lineItems = invoice.line_items || invoice.lineItems || [];
            lineItems.forEach(item => {
              const ticketNum = item.ticket_number || item.ticketNumber || item.orgTicketNumber;
              if (ticketNum) {
                ticketInvoiceMap[ticketNum] = {
                  invoiceId: invoice.invoice_number || invoice.invoiceNumber,
                  invoicePaid: invoice.status === 'PAID'
                };
              }
            });
          });
        }

        const ticketsWithInvoice = fetchedTickets.map(ticket => {
          const ticketNum = ticket.orgTicketNumber || ticket.ticketNumber;
          const invoiceInfo = ticketInvoiceMap[ticketNum];
          return {
            ...ticket,
            invoiceId: invoiceInfo?.invoiceId || null,
            invoicePaid: invoiceInfo?.invoicePaid || false
          };
        });

        setTickets(ticketsWithInvoice);
      } catch (err) {
        console.error('❌ [Tickets] Error fetching tickets:', err);
        setTickets([]);
        setLastFetchCount(0);
      } finally {
        setLoadingTickets(false);
      }
    };

    if (currentUser) {
      fetchTicketsAndInvoices();
    }
  }, [currentUser, selectedOrganization, selectedTypingCenter, dateRange, customStartDate, customEndDate, page, isPlatform]);

  const filteredTickets = tickets.filter(ticket => {
    const matchesStatus = statusFilter === 'ALL' || ticket.status === statusFilter;
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' ||
      (ticket.ticketNumber && String(ticket.ticketNumber).toLowerCase().includes(searchLower)) ||
      (ticket.orgTicketNumber && String(ticket.orgTicketNumber).toLowerCase().includes(searchLower)) ||
      (ticket.customerName && String(ticket.customerName).toLowerCase().includes(searchLower)) ||
      (ticket.serviceName && String(ticket.serviceName).toLowerCase().includes(searchLower));

    return matchesStatus && matchesSearch;
  });

  const getStatusVariant = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return 'warning';
      case 'IN_PROGRESS':
      case 'INPROGRESS':
      case 'OPEN': return 'info';
      case 'COMPLETED':
      case 'CLOSED': return 'success';
      default: return 'secondary';
    }
  };

  const columns = [
    {
      accessorKey: 'ticketNumber',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Ticket" />,
      cell: ({ row }) => (
        <span className="font-medium text-primary">
          {row.original.orgTicketNumber || row.original.ticketNumber || '-'}
        </span>
      ),
    },
    {
      accessorKey: 'customerName',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Customer" />,
      cell: ({ row }) => row.original.customerName || '-',
    },
    {
      accessorKey: 'organizationName',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Organization" />,
      cell: ({ row }) => row.original.customerOrganisation || row.original.organizationName || '-',
    },
    {
      accessorKey: 'serviceName',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Service" />,
      cell: ({ row }) => row.original.serviceName || row.original.workflow || '-',
    },
    {
      accessorKey: 'totalCost',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Cost (AED)" className="text-right" />,
      cell: ({ row }) => {
        const cost = row.original.totalCost || row.original.ticketCost || row.original.cost;
        return (
          <span className="text-right block">
            {cost ? parseFloat(cost).toFixed(2) : '-'}
          </span>
        );
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
      cell: ({ row }) => {
        const status = row.original.status || 'UNKNOWN';
        const variant = getStatusVariant(status);
        return (
          <Badge variant={variant} className="text-xs">
            {status}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'invoiceId',
      header: 'Invoice',
      cell: ({ row }) => {
        if (row.original.invoiceId) {
          return (
            <Badge variant={row.original.invoicePaid ? 'success' : 'warning'} className="text-xs">
              {row.original.invoiceId}
            </Badge>
          );
        }
        return <span className="text-muted-foreground">-</span>;
      },
    },
  ];

  const dateRangeOptions = [
    { value: '24_HR', label: '24 HR' },
    { value: '7_DAYS', label: '7 Days' },
    { value: '30_DAYS', label: '30 Days' },
    { value: '90_DAYS', label: '90 Days' },
  ];

  const disablePrev = page === 0;
  const disableNext = lastFetchCount < PAGE_SIZE;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--tenant-secondary)' }}>Tickets</h1>
        {isPlatform && (
          <Badge variant="destructive" className="text-xs">
            All Centers (ADMIN)
          </Badge>
        )}
      </div>

      {/* Date Range Filter */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              Date Range:
            </span>
            <div className="flex gap-1">
              {dateRangeOptions.map((option) => (
                <Button
                  key={option.value}
                  variant={dateRange === option.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setDateRange(option.value);
                    setPage(0);
                  }}
                  className="text-xs"
                  style={dateRange === option.value ? { backgroundColor: 'var(--tenant-secondary)', borderColor: 'var(--tenant-secondary)' } : { color: 'var(--tenant-secondary)', borderColor: 'var(--tenant-secondary)' }}
                >
                  {option.label}
                </Button>
              ))}
              <Button
                variant={dateRange === 'CUSTOM' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCustomDateOpen(true)}
                className="text-xs"
              >
                Custom
              </Button>
            </div>
            {dateRange === 'CUSTOM' && customStartDate && customEndDate && (
              <span className="text-xs text-muted-foreground">
                {customStartDate} to {customEndDate}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Custom Date Dialog */}
      <Dialog open={customDateOpen} onOpenChange={setCustomDateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Date Range</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomDateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (customStartDate && customEndDate) {
                  setDateRange('CUSTOM');
                  setPage(0);
                  setCustomDateOpen(false);
                }
              }}
              disabled={!customStartDate || !customEndDate}
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex gap-3 flex-wrap items-end">
            {/* Typing Center Filter (Admin Only) */}
            {isPlatform && (
              <div className="space-y-1.5">
                <Label className="text-xs">Typing Center</Label>
                <Select
                  value={selectedTypingCenter}
                  onValueChange={(value) => {
                    setSelectedTypingCenter(value);
                    setSelectedOrganization('ALL');
                    setPage(0);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select center" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Centers</SelectItem>
                    {typingCenters.map(center => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Organization Filter */}
            <div className="space-y-1.5">
              <Label className="text-xs">Organization</Label>
              <Select
                value={selectedOrganization}
                onValueChange={(value) => {
                  setSelectedOrganization(value);
                  setPage(0);
                }}
                disabled={loadingOrganizations}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Organizations</SelectItem>
                  {organizations.map(org => (
                    <SelectItem key={org.organizationId || org.id} value={org.organizationId || org.id}>
                      {org.organizationName || org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-1.5">
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Statuses</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Search */}
            <div className="space-y-1.5 flex-1 min-w-[200px]">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Ticket # or Customer"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mt-3">
            Showing {filteredTickets.length} tickets
          </p>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <DataTable
            columns={columns}
            data={filteredTickets}
            loading={loadingTickets}
            showPagination={false}
            showSearch={false}
            emptyMessage="No tickets found matching the filters"
            branded
          />

          {/* Custom Pagination */}
          <div className="flex items-center justify-end gap-2 p-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={disablePrev}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Prev
            </Button>
            <span className="text-sm text-muted-foreground px-2">
              Page {page + 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={disableNext}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Tickets;
